/**
 * Shared Zod schemas for dataset references in LLM-facing tool input schemas.
 *
 * Every dataset-producing tool stages its result under an `outputDataset`; every
 * dataset-consuming tool receives a `inputDataset` that points back at one of
 * those staged datasets. These primitives centralize that vocabulary so tool
 * schemas don't redeclare the same `z.string().describe(...)` slot with
 * drifting descriptions. Call sites override via Zod's native `.describe(...)`
 * / `.optional()` / `.min(...)` when they need tool-specific framing.
 *
 * Descriptions are intentionally tool-agnostic. Cross-tool references
 * ("use `transformDataset` to filter first", "paired with `getDatapoints`")
 * belong in each tool's `ToolGuidance` / `instructions` block — that's where
 * `.mention` resolves without the tool-registration import cycle that
 * plagues schema modules.
 */
import { z } from 'zod';

/** LLM-facing input: stage the produced dataset under this key. */
export const outputDatasetSchema = z
  .string()
  .describe('Unique key under which the produced dataset is staged in chat history for downstream tools to reference.');

/**
 * LLM-facing input: reference a dataset previously staged by an upstream tool.
 *
 * Use this schema directly when a tool takes a single generic dataset input.
 * When a tool needs **role-specific guidance** for the LLM (e.g. "left vs
 * right" in a join, or "source dataset to project" in a render tool),
 * call `.describe(...)` on the call site to override the generic
 * description — this is intentional and helps the LLM pick the right
 * argument for each slot. The override is purely additive prose; the
 * `z.string()` validation and the dataset-resolution semantics are
 * unaffected.
 */
export const inputDatasetSchema = z
  .string()
  .describe('Reference to a dataset previously staged in chat history by an upstream data-producing tool.');

/**
 * Compound reference to project a column from a previously staged dataset.
 *
 * Shared by filter-leaf `sourceRef` (value chaining for `in`/`notIn` ops) and
 * chart-series overlays. Both fields enforce `min(1)` to reject empty-string
 * references. Call sites re-describe fields where domain-specific framing
 * matters.
 */
export const sourceRefSchema = z.object({
  inputDataset: inputDatasetSchema.min(1),
  column: z.string().min(1).describe('Column to project from the referenced dataset.'),
});
/**
 * Separator between an `inputDataset` key and a `column` name in the
 * qualified-column form: `${inputDataset}${QUALIFIED_COLUMN_SEPARATOR}${column}`.
 */
export const QUALIFIED_COLUMN_SEPARATOR = '::';

/**
 * Build a qualified column name: `${inputDataset}::${column}`.
 *
 * Used to disambiguate same-named columns from different upstream
 * datasets when merging chart series from multiple `sourceRef`s — both
 * the chart adapter (`buildMergedChartOutput`) and the row-merge helper
 * (`mergeDatasetsByXaxis`) project columns under this name to avoid
 * collisions.
 */
export function qualifyColumn(inputDataset: string, column: string): string {
  return `${inputDataset}${QUALIFIED_COLUMN_SEPARATOR}${column}`;
}
